import { getOrdinal, SMap, templateResolver, toPascalCase } from '@avantia/client-and-server-utilities';
import { HpValidationResult } from '@avantia/form-validation';
import { lodash } from '@avantia/lodash';
import { HpEither, HpError } from '@avantia/logging-and-errors';
import {
  createControlId,
  createQuestionPanelId,
  getIdentifierConfigDefaultValues,
  HpAnswer,
  HpConditionalQuestionText,
  HpQuestionFormatTypes,
  identifierConfigChanged
} from '@avantia/questionset-model';
import $ from 'jquery';
import { default as React, ReactElement } from 'react';
import { Col, Row } from 'react-bootstrap';
import { dynamicButtonClickAction } from '../../actions/dynamicButtonClickAction';
import { HpDynamicButtonClickPayload } from '../../actions/dynamicButtonClickActionTypes';
import { HpSetActiveSectionActionPayload } from '../../actions/eventActionTypes';
import { fieldChangedAction } from '../../actions/fieldChangedAction';
import { fluentAnswerClickAction } from '../../actions/fluentAnswerClickAction';
import { HpFluentAnswerClickPayload } from '../../actions/fluentAnswerClickActionTypes';
import { getAnswersAction } from '../../actions/getAnswersAction';
import { HpGetAnswersActionPayload } from '../../actions/getAnswersActionTypes';
import { getQuestionSetAction } from '../../actions/getQuestionSetAction';
import { HpGetQuestionSetActionProps } from '../../actions/getQuestionSetActionTypes';
import { getQuestionSetsAction } from '../../actions/getQuestionSetsAction';
import { HpGetQuestionSetsActionProps } from '../../actions/getQuestionSetsActionTypes';
import { helpClickAction } from '../../actions/helpClickAction';
import { HpHelpClickPayload } from '../../actions/helpClickActionTypes';
import { navigationChangedAction } from '../../actions/navigationChangedAction';
import { HpNavigationChangedPayload } from '../../actions/navigationChangedActionTypes';
import { setActiveSectionAction } from '../../actions/setActiveSectionAction';
import { HpClientDebugFlags } from '../../clientDebugFlags';
import { registerConfigurationAction } from '../../config/registerClientConfig';
import { getCustomFunction } from '../../customFunctionRegistry';
import {
  createBlankAnswer,
  getQuestionText,
  HpButtonTextResult,
  HpQuestionTextResult
} from '../../reducers/questionSetTools';
import { applyChanges } from '../../reducers/reducerLibrary';
import { createCookieStore, createFakeStore, HpCookieStore } from '../../tools/clientCookies';
import { convertSkewerToCamelCase, getControlValue } from '../../tools/utilities';
import {
  HpClientAnswerMap,
  HpClientButtonData,
  HpClientButtonTextAndStyle,
  HpClientInputTextAndStyle,
  HpClientQuestion,
  HpClientQuestionData,
  HpClientQuestionMap,
  HpClientQuestionUI,
  HpClientSection,
  HpClientSectionsUiMap,
  HpClientSectionUI,
  HpClientStandardButtonTextAndStyle,
  HpClientStandardInputTextAndStyle,
  HpCreateCustomGroupHeadingFunction,
  HpCustomSectionTitle,
  HpFieldChangedPayload,
  HpGlobalEngineState,
  HpHelpInfoUI,
  HpQuestionDisplayState,
  HpQuestionIdGroup,
  HpRedirectToErrorPageActionProps,
  HpViewportState
} from '../../types';
import { HpLoadingPageTypes } from '../../types/ajaxCallActionTypes';
import { HpCommonButtonControlProps, HpInputControl, HpInputControlProps } from '../../types/inputControlTypes';
import { HpQuestionSetPageDispatchProps, HpQuestionSetPageStateProps } from '../../types/questionSetPagePropTypes';
import * as FluentControls from '../formControls/fluent/index';
import { selectButtonControlToUse, selectControlToUse } from '../formControls/inputControl';
import * as StandardControls from '../formControls/standard/index';
import { HpSectionControlFunction } from '../formControls/standard/index';
import { redirectToErrorPageAction } from '../pageComponentUtilities';
import { HpLoadingWidgetFunction, LoadingWidgetOverlay } from '../widgets';
import {
  HpCreateQuestionControlsProps,
  HpMapStateToPropsFunc,
  HpPageEvents,
  HpQuestionControlResult,
  HpQuestionSetPageMapDispatchToPropsFunc,
  HpSectionGroupRootControlFunction
} from './questionSetPageTypes';

export type HpControlFunction = () => JSX.Element;

let developerModeEnabled = false;
let cookies: HpCookieStore;
let { groupStartNumber } = getIdentifierConfigDefaultValues();

const controlTypeCustomSuffix = '-custom';
const controlTypeMap: SMap<SMap<HpControlFunction>> = {
  fluent: FluentControls as unknown as SMap<HpControlFunction>,
  standard: StandardControls as unknown as SMap<HpControlFunction>
};

registerConfigurationAction((config) => {
  ({ developerModeEnabled } = config);
  cookies = developerModeEnabled ? createCookieStore({ disableNameToKeyMapping: true }) : createFakeStore();
});

identifierConfigChanged(({ groupStartNumber: gsn }) => {
  groupStartNumber = gsn;
});

export interface HpRegisterCustomControlsProps {
  controls: SMap<HpControlFunction>;
  type: HpQuestionFormatTypes;
}

export function registerCustomControls({ controls, type }: HpRegisterCustomControlsProps): void {
  const compositeType = `${type}${controlTypeCustomSuffix}`;
  if (controlTypeMap[compositeType]) {
    throw new HpError(`You cannot create controls for type "${type}" because they already exist.`);
  }

  controlTypeMap[compositeType] = { ...controls };
}

export interface HpRegisterOverrideControlsProps {
  controlType: HpQuestionFormatTypes;
  type: string;
}

export function registerOverrideControls({ controlType, type }: HpRegisterOverrideControlsProps): void {
  if (controlTypeMap[type]) {
    throw new HpError(`You cannot create controls for type "${type}" because they already exist.`);
  }

  controlTypeMap[type] = controlTypeMap[controlType];
}

export function createQuestionControls({
  allAnswers,
  allQuestions,
  displayState,
  level,
  page,
  pageType,
  productId,
  questionFormats,
  questionIdNodes,
  validationResult,
  viewport
}: HpCreateQuestionControlsProps): HpQuestionControlResult {
  const elements: React.ReactElement[] = []; // we need a reference that explicitly uses React or the unit tests will fail.
  const elementMap: SMap<ReactElement> = {};
  const { questionsUI } = displayState;
  const showStandardFormat = questionFormats.indexOf('standard') >= 0;
  let singleCount = 0,
    groupCount = 0;
  for (let index = 0; index < questionIdNodes.length; index++) {
    const questionIdNode = questionIdNodes[index];
    const isGroup = typeof questionIdNode === 'object';
    if (!isGroup) {
      singleCount++;
      const key = questionIdNode as string;
      const questionUI = questionsUI[key];
      const question: HpClientQuestion = allQuestions[key];
      const { data } = question;
      const { textAndStyle } = data;
      const styleToControlTypeMap: SMap<string> = {};
      let standardControlType: string | undefined;
      let fluentControlType: string | undefined;
      let standardButtonText: string | HpConditionalQuestionText[] | undefined;

      lodash.keys(textAndStyle).forEach((type) => {
        const { controlType, buttonText } = textAndStyle[type] as HpClientStandardButtonTextAndStyle;
        if (controlType) {
          styleToControlTypeMap[type] = controlType;
          if (type === 'standard') {
            standardControlType = controlType;
            if (buttonText) {
              standardButtonText = buttonText;
            }
          } else if (type === 'fluent') {
            fluentControlType = controlType;
          }
        }
      });

      let element: ReactElement | undefined;
      const isHidden = standardControlType === 'Hidden' || fluentControlType === 'Hidden';
      if (!isHidden) {
        if (standardButtonText) {
          if (showStandardFormat) {
            element = createButtonControl({
              getControl: (type) => getControl<HpControlFunction>(type, styleToControlTypeMap[type]),
              key,
              page,
              question,
              questionFormats,
              textAndStyle: textAndStyle as HpClientButtonTextAndStyle,
              ui: questionUI,
              validationResult,
              viewport
            });
          }
        } else {
          element = createInputControl({
            getControl: (type) => getControl<HpControlFunction>(type, styleToControlTypeMap[type]),
            key,
            page,
            pageType,
            productId,
            question,
            questionFormats,
            riskAnswers: allAnswers,
            ui: questionUI,
            validationResult,
            viewport
          });
        }
      }

      if (element) {
        elements.push(element);
        elementMap[question.questionId] = element;
      }
    } else {
      groupCount++;
      if (showStandardFormat) {
        addGroup(questionIdNode);
      }
    }
  }

  let els = elements;
  if (level === 0 && singleCount === 0 && groupCount > 0) {
    const node = questionIdNodes[0] as HpQuestionIdGroup;
    const SectionControl = getControl<HpSectionGroupRootControlFunction>(
      'standard',
      toPascalCase(convertSkewerToCamelCase(node.groupId.replace(/_/g, '-'))) + 'RootCustom'
    );
    if (SectionControl) {
      els = [<SectionControl key={node.groupId} id={node.groupId} groups={elements} />];
    }
  }

  return { elements: els, elementMap };

  function addGroup(questionIdGroup: HpQuestionIdGroup) {
    const groupControl = createStandardGroupControl({
      page,
      pageType,
      allQuestions,
      allAnswers,
      questionIdGroup,
      validationResult,
      displayState,
      viewport,
      questionFormats,
      level,
      elementMap,
      productId
    });

    if (groupControl) {
      const { groupId, groupNumber } = questionIdGroup;
      const groupIdWithNumber = createControlId({
        questionId: groupId,
        groupNumber: groupNumber,
        options: { noPrefix: true }
      });
      elementMap[groupIdWithNumber] = groupControl;
      elements.push(groupControl); // This needs to be done at the end, the whole group widget including nested elements needs to be created before adding it to the elements array.
    }
  }
}

export function applyStylesheetsToPage(stylesheets: string[]): void {
  const pathPrefix = '/dynamic-';
  const allStylesheets = $('head > link[rel=stylesheet]').toArray() as HTMLLinkElement[];
  const dynamicSheets = allStylesheets.filter((sheet) => sheet.href.indexOf(pathPrefix) >= 0);
  if (dynamicSheets.length > 0) {
    $(dynamicSheets).remove();
  }

  for (const sheet of stylesheets) {
    $('head').append(`<link rel="stylesheet" href="${pathPrefix}${sheet}.css" />`);
  }
}

function getControl<
  ControlFunctionT extends HpEither<
    HpEither<HpControlFunction, HpSectionControlFunction>,
    HpSectionGroupRootControlFunction
  >
>(type: HpQuestionFormatTypes, controlType: string | null | undefined): ControlFunctionT | undefined {
  if (!controlType) {
    return undefined;
  }

  const controlName = controlType + 'Control';
  let control: HpControlFunction | undefined;
  control = controlTypeMap[type][controlName];

  if (!control && controlType.endsWith('Custom')) {
    control = (controlTypeMap[type + controlTypeCustomSuffix] || {})[controlName];
  }

  return control as ControlFunctionT;
}

export const mapStateToPropsDefault: HpMapStateToPropsFunc = (
  state: HpGlobalEngineState
): HpQuestionSetPageStateProps => {
  const { questionSet, userInterface } = state;
  return { ...questionSet, userInterface };
};

export const mapDispatchToPropsDefault: HpQuestionSetPageMapDispatchToPropsFunc = (
  dispatch: (payload: any) => any
): HpQuestionSetPageDispatchProps => {
  return {
    dynamicButtonClick: (payload: HpDynamicButtonClickPayload): void => dispatch(dynamicButtonClickAction(payload)),
    fieldChanged: (payload: HpFieldChangedPayload): void => dispatch(fieldChangedAction(payload)),
    fluentAnswerClick: (payload: HpFluentAnswerClickPayload): void => dispatch(fluentAnswerClickAction(payload)),
    getAnswers: (payload: HpGetAnswersActionPayload): void => dispatch(getAnswersAction(payload)),
    getQuestionSet: (payload: HpGetQuestionSetActionProps): void => dispatch(getQuestionSetAction(payload)),
    getQuestionSets: (payload: HpGetQuestionSetsActionProps): void => dispatch(getQuestionSetsAction(payload)),
    helpClick: (payload: HpHelpClickPayload): void => dispatch(helpClickAction(payload)),
    navigationChanged: (payload: HpNavigationChangedPayload): void => dispatch(navigationChangedAction(payload)),
    redirectToErrorPage: (props: HpRedirectToErrorPageActionProps): void =>
      redirectToErrorPageAction({ ...props, dispatch }),
    setActiveSection: (payload: HpSetActiveSectionActionPayload): void => dispatch(setActiveSectionAction(payload))
  };
};

export function getLoadingPageElement(
  ajaxCallsInProgress: number,
  ajaxCallsMap: SMap<HpLoadingPageTypes>,
  viewport: HpViewportState,
  LoadingWidget: HpLoadingWidgetFunction
): JSX.Element | undefined {
  let isLoading = !(!ajaxCallsInProgress || !ajaxCallsMap || !lodash.keys(ajaxCallsMap).length);
  if (!isLoading) {
    if (developerModeEnabled) {
      const showLoadingPageType = cookies.getItem('hpShowLoadingPageType') as HpLoadingPageTypes;
      if (showLoadingPageType === 'overlay' || showLoadingPageType === 'page') {
        ajaxCallsMap = { developer: showLoadingPageType as HpLoadingPageTypes };
        isLoading = true;
      } else if (showLoadingPageType) {
        throw new Error(`Invalid LoadingPageTypes: "${showLoadingPageType}". Valid options are 'page' or 'overlay'.`);
      }
    }

    if (!isLoading) {
      return;
    }
  }

  let loadingPage: JSX.Element | undefined;
  for (const pageType of lodash.values(ajaxCallsMap)) {
    if (pageType === 'page') {
      loadingPage = <LoadingWidget viewport={viewport} />;
      break;
    }

    if (pageType === 'overlay') {
      loadingPage = <LoadingWidgetOverlay viewport={viewport} />;
    }
  }

  return loadingPage;
}

export type HpGetControlFunction = (type: HpQuestionFormatTypes) => HpInputControl<HpInputControlProps> | undefined;

interface CreateInputControlProps {
  getControl: HpGetControlFunction;
  key: string;
  page: HpPageEvents;
  pageType: string;
  productId: string;
  question: HpClientQuestion;
  questionFormats: HpQuestionFormatTypes[];
  riskAnswers: HpClientAnswerMap;
  ui: HpClientQuestionUI;
  validationResult: HpValidationResult;
  viewport: HpViewportState;
}

function createInputControl({
  getControl,
  key,
  page,
  pageType,
  productId,
  question,
  questionFormats,
  riskAnswers,
  ui,
  validationResult,
  viewport
}: CreateInputControlProps): ReactElement | undefined {
  const { errors } = validationResult;
  let answer: HpAnswer = riskAnswers[key];
  if (!answer) {
    answer = createBlankAnswer(HpClientDebugFlags.Controls);
  }

  const controlResult = selectControlToUse(questionFormats, getControl);
  const { data, disabled, isMultiSelect, questionId } = question;

  if (controlResult) {
    const { Control, formatType } = controlResult;
    const { answerSource, answerValue } = getControlValue(answer, question);
    const questionText = getQuestionText({
      question,
      questionUI: ui,
      questionFormats: page.props.questionFormats
    }) as HpQuestionTextResult;

    const attributes: HpClientQuestion & HpInputControlProps = Object.assign({}, question, {
      id: questionId,
      ...questionText,
      isMultiSelect,
      value: answerValue,
      formatType,
      answerSource,
      answers: riskAnswers,
      data: data as HpClientQuestionData,
      question,
      answer,
      productId,
      errors,
      viewport,
      ui,
      disabled: ui.disabled || disabled,
      pageProps: page.props,
      pageType,
      onChange: page.fieldChanged,
      onFocus: page.fieldChanged,
      onBlur: page.fieldChanged,
      onClick: page.fieldClicked
    });

    return <Control key={questionId} {...attributes} />;
  }

  throw new Error(`The control specified by question "${question.questionId}" is not valid.`);
}

interface CreateButtonControlProps {
  getControl: HpGetControlFunction;
  key: string;
  page: HpPageEvents;
  question: HpClientQuestion;
  questionFormats: HpQuestionFormatTypes[];
  textAndStyle: HpClientButtonTextAndStyle;
  ui: HpClientQuestionUI;
  validationResult: HpValidationResult;
  viewport: HpViewportState;
}

function createButtonControl({
  key,
  page,
  question,
  questionFormats,
  textAndStyle,
  ui,
  validationResult,
  viewport,
  getControl
}: CreateButtonControlProps): ReactElement | undefined {
  const addRowColContainer = false;
  const { data, questionId } = question;
  const controlResult = selectButtonControlToUse(questionFormats, getControl);

  if (controlResult) {
    const { buttonText } = getQuestionText({
      question,
      questionUI: ui,
      questionFormats: page.props.questionFormats
    }) as HpButtonTextResult;

    const { Control, formatType } = controlResult;
    const { type } = textAndStyle[formatType] as HpClientStandardButtonTextAndStyle;
    const attributes: Partial<HpCommonButtonControlProps> = {
      id: questionId,
      type,
      buttonText,
      data: data as HpClientButtonData,
      question,
      errors: validationResult.errors,
      viewport
    };

    const ctrl = (
      <Control
        key={questionId}
        {...(attributes as HpCommonButtonControlProps)}
        formatType={formatType}
        onClick={(e): void => page.buttonClicked(e, { type: 'button-action', question })}
      />
    );

    return addRowColContainer ? (
      <Row key={createQuestionPanelId(key)}>
        <Col>{ctrl}</Col>
      </Row>
    ) : (
      ctrl
    );
  }
}

interface CreateStandardGroupControlProps {
  allAnswers: HpClientAnswerMap;
  allQuestions: HpClientQuestionMap;
  displayState: HpQuestionDisplayState;
  elementMap: SMap<ReactElement>;
  level: number;
  page: HpPageEvents;
  pageType: string;
  productId: string;
  questionFormats: HpQuestionFormatTypes[];
  questionIdGroup: HpQuestionIdGroup;
  validationResult: HpValidationResult;
  viewport: HpViewportState;
}

function createStandardGroupControl({
  allAnswers,
  allQuestions,
  displayState,
  elementMap,
  level,
  page,
  pageType,
  productId,
  questionFormats,
  questionIdGroup,
  validationResult,
  viewport
}: CreateStandardGroupControlProps): ReactElement | null {
  const { groupId, groupKey, groupNumber, questions: questionIds } = questionIdGroup;
  const { elements: controls, elementMap: childMap } = createQuestionControls({
    allAnswers,
    allQuestions,
    displayState,
    level: level + 1,
    page,
    pageType,
    productId,
    questionFormats,
    questionIdNodes: questionIds,
    validationResult,
    viewport
  });

  Object.assign(elementMap, childMap);
  const { questionsUI, sections, sectionsUI } = displayState;
  const sectionUI = sectionsUI[groupId];
  const section = sections[groupId];
  if (!section) {
    throw new Error(`There is no Section with ID "${groupId}".`);
  }

  const { controlType, title } = section;
  if (sectionUI.isVisible) {
    const SectionControl = getControl<HpSectionControlFunction>('standard', controlType || 'Section');
    if (!SectionControl) {
      throw new Error(`There is no controlType ${controlType} for Section with ID "${groupId}".`);
    }

    // eslint-disable-next-line prefer-const
    let { id, helpInfo } = getIdAndHelpInfo(groupId, groupNumber, sectionsUI);
    const sectionTitle = createSectionTitle({
      title,
      groupNumber,
      questionSetId: page.props.questionSetId,
      answers: allAnswers,
      questions: allQuestions,
      section
    });

    if (groupKey) {
      // It means it's a repeating group. If we use 'id' it will be the same for each group.
      // The groupKey contains a group suffix (~G01)
      id = groupKey;
    }

    return (
      <SectionControl
        answers={allAnswers}
        controlMap={childMap}
        controls={controls}
        errors={validationResult.errors}
        helpInfoUI={helpInfo}
        id={id}
        index={groupNumber}
        key={id}
        label={sectionTitle as string}
        questions={allQuestions}
        questionsUiMap={questionsUI}
        section={section}
      />
    );
  }

  return null;
}

interface HpGetIdAndHelpInfoResult {
  helpInfo: HpHelpInfoUI | undefined;
  id: string;
}

function getIdAndHelpInfo(
  groupId: string,
  number: number | undefined,
  sectionsUI: HpClientSectionsUiMap
): HpGetIdAndHelpInfoResult {
  const sectionUI = sectionsUI[groupId];

  if (number === undefined) {
    return { id: groupId, helpInfo: sectionUI.helpInfo };
  }

  const sid = createControlId({ questionId: groupId, groupNumber: number, options: { noPrefix: true } });
  const sectionGroupUI: Partial<HpClientSectionUI> = sectionsUI[sid];
  const helpInfo: Partial<HpHelpInfoUI> = applyChanges<Partial<HpHelpInfoUI>>(
    (sectionGroupUI ? sectionGroupUI.helpInfo : null) || {},
    (sectionUI ? sectionUI.helpInfo : null) || {}
  );

  return { id: `${groupId}-${number}`, helpInfo };
}

interface HpCreateSectionTitleProps {
  answers: HpClientAnswerMap;
  groupNumber: number | undefined;
  questions: HpClientQuestionMap;
  questionSetId: string;
  section: HpClientSection;
  title: string;
}

function createSectionTitle({
  groupNumber,
  questionSetId,
  answers,
  questions,
  title,
  section
}: HpCreateSectionTitleProps): string | HpCustomSectionTitle {
  if (typeof title === 'string' && title.startsWith('{{function:')) {
    const funcName = title.split(':')[1].slice(0, -2);
    const func = getCustomFunction(questionSetId, funcName) as HpCreateCustomGroupHeadingFunction;
    return func({ groupNumber, answers, questions, section });
  }

  if (groupNumber !== undefined) {
    const natural = groupNumber + (groupStartNumber === 0 ? 1 : 0);
    const ordinal = getOrdinal(natural, { lowerCase: false });
    return templateResolver(`${title}`, {
      index: groupNumber,
      natural,
      Ordinal: ordinal,
      ordinal: ordinal.toLowerCase()
    });
  }

  return title;
}

/**
 * This creates a blank question for non-question based controls (e.g. navigation)
 * @returns A partial question masquerading as a question.
 */
export function createNonQuestion(): HpClientQuestion {
  const standard: HpClientStandardInputTextAndStyle = {
    controlType: 'DropDownList',
    questionText: '.'
  };
  const textAndStyle: HpClientInputTextAndStyle = { standard };
  const partQuestion: Partial<HpClientQuestion> = {
    questionId: 'question-list',
    data: { textAndStyle } as HpClientQuestionData
  };
  return partQuestion as HpClientQuestion;
}
